import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { logger } from '../lib/logger';
import Header from '../components/dashboard/Header';
import WelcomeGuide from '../components/dashboard/WelcomeGuide';
import MobileBottomNav from '../components/dashboard/MobileBottomNav';
import DataHealthBadge from '../components/dashboard/DataHealthBadge';
import DashboardSections from '../components/dashboard/DashboardSections';
import { MobileMenu, useMobileMenu } from '../components/dashboard/TopNav';
import { pageForPath, pathForSection } from '../components/dashboard/nav';
import { useScannerStream } from '../lib/useScannerStream';
import { useCoinData } from '../lib/useCoinData';
import { useMediaQuery } from '../lib/useMediaQuery';
import useDecisionSound from '../lib/useDecisionSound';
import { useAutoTrade } from '../lib/useAutoTrade';
import { toast } from 'sonner';

export default function Dashboard() {
    const [timeframe, setTimeframe] = useState('short');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selected, setSelected] = useState('BTCUSDT');
    const [alerts, setAlerts] = useState([]);
    const [watchlist, setWatchlist] = useState([]);
    const [regime, setRegime] = useState(null);
    const [onlyFavorites, setOnlyFavorites] = useState(false);
    const [onlyBinance, setOnlyBinance] = useState(false);
    const [openPositions, setOpenPositions] = useState([]);

    // Sayfa = URL. Üst menüden (masaüstü) ya da alt bar / hamburger menüden (mobil) seçilen sayfa tek başına açılır.
    const isMobile = useMediaQuery('(max-width: 767px)');
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const page = pageForPath(pathname);
    const menu = useMobileMenu();
    const [backPath, setBackPath] = useState(null);
    useEffect(() => {  // bilinmeyen yol → ana sayfa
        const clean = pathname.replace(/\/+$/, '') || '/';
        if (clean !== page.path) navigate('/', { replace: true });
    }, [pathname, page.path, navigate]);

    // Subscribe to backend WS push stream — replaces interval polling.
    const { data: streamData, favorites: streamFavs, tickPrices, status: streamStatus } = useScannerStream({ enabled: true });

    // Map a stream payload (or REST response shape) into the enriched rows the table needs.
    const enrichResults = useCallback((results) => {
        return (results || []).map((r) => ({
            ...r,
            funding_rate_pct: r.derivatives?.funding_rate_pct,
            ls_ratio: r.derivatives?.long_short_ratio,
            profit_pct: r.recommendation?.profit_pct ?? 0,
            rr_ratio: r.recommendation?.rr_ratio ?? 0,
            rec_score:
                r.recommendation?.action === 'LONG'
                    ? (r.recommendation?.confidence ?? 0)
                    : r.recommendation?.action === 'SHORT'
                      ? -(r.recommendation?.confidence ?? 0)
                      : 0,
            bias_score:
                r.bias?.direction === 'long'
                    ? (r.bias?.confidence ?? 0)
                    : r.bias?.direction === 'short'
                      ? -(r.bias?.confidence ?? 0)
                      : 0,
        }));
    }, []);

    // Apply incoming WS updates for the active timeframe.
    useEffect(() => {
        const update = streamData?.[timeframe];
        if (!update) return;
        setRows(enrichResults(update.results));
        setAlerts(update.alerts || []);
        setLoading(false);
        setRefreshing(false);
    }, [streamData, timeframe, enrichResults]);

    // Toast on favorite-level crossings (TP1/TP2/SL)
    const seenEventIds = React.useRef(new Set());
    useEffect(() => {
        const events = streamFavs?.events || [];
        if (!events.length) return;
        for (const ev of events) {
            const key = `${ev.symbol}|${ev.level}|${ev.crossed_at}`;
            if (seenEventIds.current.has(key)) continue;
            seenEventIds.current.add(key);
            const emoji = ev.level === 'STOP' ? '⚠️' : '🎯';
            toast(`${emoji} ${ev.symbol.replace('USDT', '')} ${ev.level} seviyesine değdi — ${ev.price?.toFixed(ev.price < 1 ? 6 : 2)}${ev.short_id ? ` · #${ev.short_id}` : ''}`, {
                description: ev.summary,
            });
        }
    }, [streamFavs]);

    const load = useCallback(
        async (showToast = false) => {
            // Manual refresh: REST fallback in case the stream is degraded; also
            // re-fetches the user's watchlist (which the WS payload doesn't include).
            setRefreshing(true);
            if (rows.length === 0) setLoading(true);
            try {
                const scanRes = await apiClient.get('/market/top100', { params: { timeframe, limit: 50 } });
                setRows(enrichResults(scanRes.data.results));
                setWatchlist(scanRes.data.watchlist || []);
                if (scanRes.data.regime) setRegime(scanRes.data.regime);
                if (showToast) toast.success('Tarama güncellendi');
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'Veri alınamadı');
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [timeframe, enrichResults, rows.length]
    );

    const toggleFavorite = useCallback(
        async (symbol, makeFavorite) => {
            try {
                if (makeFavorite) {
                    await apiClient.post('/watchlist', { symbol });
                    toast.success(`${symbol.replace('USDT', '')} favorilere eklendi`);
                } else {
                    await apiClient.delete(`/watchlist/${symbol}`);
                    toast.success(`${symbol.replace('USDT', '')} favorilerden çıkarıldı`);
                }
                setRows((prev) => prev.map((r) => (r.symbol === symbol ? { ...r, is_favorite: makeFavorite } : r)));
                setWatchlist((prev) =>
                    makeFavorite ? [...new Set([...prev, symbol])] : prev.filter((s) => s !== symbol)
                );
                if (makeFavorite) load(false);
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'İşlem başarısız');
            }
        },
        [load]
    );

    const goTo = useCallback((path) => {
        navigate(path);
        setBackPath(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [navigate]);

    // Coin seçince Coin sayfası açılır (listeden geldiyse "Geri" aynı yere, aynı kaydırma konumuna döner).
    const backScroll = React.useRef(0);
    const pendingScroll = React.useRef(null);
    const selectCoin = useCallback(
        (symbol) => {
            setSelected(symbol);
            if (page.path !== '/coin') {
                backScroll.current = window.scrollY;
                setBackPath(page.path);
                navigate('/coin');
                window.scrollTo({ top: 0 });
            }
        },
        [navigate, page.path]
    );

    // "Geri": önceki sayfaya aynı kaydırma konumunda dön.
    const goBack = useCallback(() => {
        if (!backPath) return;
        pendingScroll.current = backScroll.current;
        navigate(backPath);
        setBackPath(null);
    }, [backPath, navigate]);
    useEffect(() => {
        if (pendingScroll.current === null) return;
        const y = pendingScroll.current;
        pendingScroll.current = null;
        requestAnimationFrame(() => window.scrollTo({ top: y }));
    }, [pathname]);

    // Sayfa içi bağlantılar (ör. "Telegram bağlı değil — önce bağla") ilgili sayfayı açar.
    useEffect(() => {
        const onNavigate = (e) => {
            const section = e.detail?.section;
            if (section) goTo(pathForSection(section));
        };
        window.addEventListener('cds:navigate', onNavigate);
        return () => window.removeEventListener('cds:navigate', onNavigate);
    }, [goTo]);

    const handleSearchSelect = useCallback(
        async (symbol) => {
            setSelected(symbol);
            const inScan = rows.some((r) => r.symbol === symbol);
            const inWatch = watchlist.includes(symbol);
            if (!inScan && !inWatch) {
                await toggleFavorite(symbol, true);
            }
        },
        [rows, watchlist, toggleFavorite]
    );

    useEffect(() => {
        // Initial REST fetch to populate watchlist (which the WS push doesn't carry)
        // and to render data even before the WS handshake completes.
        load(false);
    }, [timeframe]); // eslint-disable-line

    // Poll open positions so the scanner can badge rows the user has active trades on
    useEffect(() => {
        let cancelled = false;
        const loadPositions = async () => {
            try {
                const { data } = await apiClient.get('/positions');
                if (!cancelled) setOpenPositions(data?.positions || []);
            } catch (e) {
                logger.warn('open positions load failed', e);
            }
        };
        loadPositions();
        const id = setInterval(loadPositions, 20000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const openPositionSymbols = useMemo(
        () => new Set((openPositions || []).map((p) => p.symbol)),
        [openPositions]
    );

    // Selected coin's fresh analysis wins in every list → radar/strips/table never contradict CoinDetail.
    const { data: selectedDetail } = useCoinData(selected, timeframe, 15000);
    const liveRows = useMemo(() => {
        if (!selectedDetail) return rows;
        const { candles, multi_tf, multi_tf_status, multi_tf_other_action, multi_tf_other_confidence, ...fresh } = selectedDetail;
        return rows.map((r) => (r.symbol === fresh.symbol ? enrichResults([{ ...r, ...fresh }])[0] : r));
    }, [rows, selectedDetail, enrichResults]);

    const onSoundEvent = useCallback((ev) => {
        toast(`${ev.emoji} ${ev.action_tr} · ${ev.symbol.replace('USDT', '')} ${ev.direction === 'short' ? '▼ AŞAĞI' : '▲ YUKARI'}`, {
            description: 'Yeni karar çıktı — detayı aç',
            action: { label: 'Aç', onClick: () => selectCoin(ev.symbol) },
            duration: 12000,
        });
    }, [selectCoin]);
    const sound = useDecisionSound(rows, onSoundEvent);
    const at = useAutoTrade();

    const order = page.sections;
    const verdictBySymbol = useMemo(() => Object.fromEntries(liveRows.filter((r) => r.verdict).map((r) => [r.symbol, r.verdict])), [liveRows]);
    const ctx = {
        selected, timeframe, tickPrices, rows, liveRows, loading, alerts, watchlist, regime, verdictBySymbol, at,
        streamFavs, streamStatus, onlyFavorites, setOnlyFavorites, onlyBinance, setOnlyBinance,
        toggleFavorite, openPositionSymbols, handleSearchSelect, selectCoin, isMobile, backTab: backPath, goBack, page,
    };

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100">
            <Header timeframe={timeframe} onTimeframeChange={setTimeframe} onRefresh={() => load(true)} refreshing={refreshing} sound={sound} onMenu={() => menu.setOpen(true)} />
            <MobileMenu open={menu.open} onOpenChange={menu.setOpen} />

            <main className="max-w-[1920px] mx-auto p-3 sm:p-6 space-y-4 sm:space-y-5 pb-20 md:pb-6" data-testid="dashboard-main" data-page={page.path}>
                {isMobile && <DataHealthBadge rows={rows} streamStatus={streamStatus} />}
                {page.path === '/' && <WelcomeGuide />}

                <DashboardSections order={order} ctx={ctx} />

                <footer className="pt-4 pb-8 text-center text-[10px] text-slate-500 uppercase tracking-[0.2em]" data-testid="footer-stream-status">
                    CDS Terminal · Yatırım Tavsiyesi Değildir · OKX + CryptoPanic ·{' '}
                    <span className={
                        streamStatus === 'open' ? 'text-emerald-400' :
                        streamStatus === 'reconnecting' ? 'text-amber-400' :
                        streamStatus === 'unauthorized' ? 'text-rose-400' : 'text-slate-500'
                    }>
                        {streamStatus === 'open' ? '● CANLI' :
                         streamStatus === 'reconnecting' ? '◌ Yeniden bağlanıyor' :
                         streamStatus === 'unauthorized' ? '✕ Yetkisiz' : '◌ Bağlanıyor'}
                    </span>
                </footer>
            </main>

            {/* Mobil sabit alt bar: sayfalar + Menü */}
            <MobileBottomNav activePath={page.path} onChange={goTo} onMenu={() => menu.setOpen(true)} />
        </div>
    );
}
